import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { loadConfig } from '../api/data-qa-api';
import { ModelTypeType } from '../data-qa-constants';
import { ConfigSummary, DataQaDataset, MmmConfig } from '../data-qa-types';
import { groupConfigsByModel, modelTypeForProductType } from '../data-qa-utils';

export interface ModelStage {
  config: MmmConfig;
  dataset: DataQaDataset;
}

export interface ModelWithStages {
  modelType: ModelTypeType;
  // Every stage config of the model, in groupConfigsByModel's stage order ('s1' before
  // 's2') -- one entry for every model except Brand Equity, whose two stages differ.
  stages: ModelStage[];
}

export interface AllModelStagesData {
  loading: boolean;
  models: ModelWithStages[];
  // Same reasoning as useChannelExportModels' failedModelLabels: an errored query is
  // neither pending nor success, so without this an export button would silently
  // re-enable and produce a workbook missing that model.
  failedModelLabels: string[];
}

// Every stage of every company model, for the all-models Excel exports. Unlike
// useChannelExportModels (one representative config per model, since Channels data is
// identical across Brand Equity's stages), this loads EVERY config. Shares the same
// queryKey shape, so it observes the cache entries usePrefetchOtherModels already
// warmed rather than triggering new fetches.
export const useAllModelStages = (
  cid: string | undefined,
  configs: ConfigSummary[]
): AllModelStagesData => {
  const groups = useMemo(() => groupConfigsByModel(configs), [configs]);

  const flatConfigs = useMemo(
    () =>
      groups.flatMap((group, groupIndex) =>
        group.configs.map(config => ({ groupIndex, configPath: config.configPath }))
      ),
    [groups]
  );

  const queries = useMemo(
    () =>
      flatConfigs.map(({ configPath }) => ({
        queryKey: ['mmmDataQALoad', cid, configPath],
        queryFn: () => loadConfig(cid ?? '', configPath),
        staleTime: Infinity,
        enabled: Boolean(cid)
      })),
    [flatConfigs, cid]
  );

  const results = useQueries({ queries });

  return useMemo(() => {
    const loading = results.some(result => result.status === 'pending');
    const stagesByGroup = groups.map(() => [] as ModelStage[]);
    const failedGroups = new Set<number>();
    flatConfigs.forEach(({ groupIndex }, i) => {
      const result = results[i];
      if (result?.status === 'success') {
        stagesByGroup[groupIndex].push({
          config: result.data.config,
          dataset: result.data.dataset
        });
      } else if (result?.status === 'error') {
        failedGroups.add(groupIndex);
      }
    });

    const models: ModelWithStages[] = [];
    const failedModelLabels: string[] = [];
    groups.forEach((group, groupIndex) => {
      const modelType = modelTypeForProductType(group.productType);
      if (failedGroups.has(groupIndex)) {
        failedModelLabels.push(group.label);
      } else if (stagesByGroup[groupIndex].length === group.configs.length) {
        models.push({ modelType, stages: stagesByGroup[groupIndex] });
      }
    });
    return { loading, models, failedModelLabels };
  }, [groups, flatConfigs, results]);
};
